import { isRuralProperty, useDebouncedState } from '@urbanx/agx-ui-components';
import { ExternalIntegrations } from '@urbanx/agx.agencies.client';
import {
  State,
  useGetDomainListingFee,
  useInvalidateUseGetDomainListingFee,
} from '@urbanx/agx.domain.client';
import { useAppSelector } from 'hooks/useAppSelector';
import { usePropertyPriceSelector } from 'hooks/usePropertyPriceSelector';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import useFormAddressSelector from 'selectors/useFormAddressSelector';
import useFormPropertyDetailsSelector from 'selectors/useFormPropertyDetailsSelector';

interface SearchDetails {
  suburb: string;
  state: State;
  postalCode: string;
  backendPrice: number;
  isRural: boolean;
  shouldSearch: boolean;
}

const useDomainSearchDetails = () => {
  const { suburb, state, postalCode } = useFormAddressSelector() ?? {};
  const backendPrice = usePropertyPriceSelector();
  const propertyDetails = useFormPropertyDetailsSelector();

  const externalIntegrations =
    useAppSelector(
      state => state.agencies?.agencyInfo?.info?.externalIntegrations
    ) ?? [];

  const domainIntegrationEnabled = useMemo(() => {
    if (!externalIntegrations) return false;

    return externalIntegrations.includes(ExternalIntegrations.DomainDotCom);
  }, [externalIntegrations]);

  const isPropertyRural = useMemo(() => {
    return isRuralProperty(propertyDetails);
  }, [propertyDetails?.landAreaDetails]);

  const [searchDetails, setSearchDetails] = useDebouncedState<SearchDetails>(
    createSearchDetails(
      domainIntegrationEnabled,
      isPropertyRural,
      suburb,
      state,
      postalCode,
      backendPrice
    ),
    300
  );

  useEffect(() => {
    const updatedSearchDetails = createSearchDetails(
      domainIntegrationEnabled,
      isPropertyRural,
      suburb,
      state,
      postalCode,
      backendPrice
    );

    if (isEqual(updatedSearchDetails, searchDetails)) return;

    setSearchDetails(updatedSearchDetails);
  }, [
    domainIntegrationEnabled,
    suburb,
    state,
    postalCode,
    isPropertyRural,
    backendPrice,
    searchDetails,
  ]);

  return searchDetails;
};

const createSearchDetails = (
  domainIntegrationEnabled: boolean,
  isPropertyRural: boolean,
  suburb?: string,
  state?: string,
  postalCode?: string,
  backendPrice?: number
): SearchDetails => {
  return {
    suburb: suburb ?? '',
    state: (state as State) ?? State.Qld,
    postalCode: postalCode ?? '',
    backendPrice: backendPrice ?? 0,
    isRural: isPropertyRural,
    shouldSearch:
      domainIntegrationEnabled && !!suburb && !!state && !!postalCode,
  };
};

const useGetPropertyDomainPrice = () => {
  const { suburb, state, postalCode, backendPrice, isRural, shouldSearch } =
    useDomainSearchDetails();

  const { data: listingFeeResponse, isPending: isDomainListingFeePending } =
    useGetDomainListingFee(
      suburb,
      state,
      postalCode,
      backendPrice,
      isRural,
      shouldSearch
    );

  const invalidateDomainListingFee = useInvalidateUseGetDomainListingFee();

  const reloadDomainPricing = useCallback(() => {
    invalidateDomainListingFee(
      suburb,
      state,
      postalCode,
      backendPrice,
      isRural
    );
  }, [
    invalidateDomainListingFee,
    backendPrice,
    isRural,
    postalCode,
    state,
    suburb,
  ]);

  return {
    domainListingFee: listingFeeResponse?.listingFee,
    domainListingRetrievalSuccessful: listingFeeResponse?.success,
    isDomainListingFeePending,
    reloadDomainPricing,
  };
};

export default useGetPropertyDomainPrice;
